import React, { useState } from 'react';
import {
  FaBell, FaBan, FaCoins, FaMedal, FaCalendarTimes,
  FaShieldAlt, FaSearch, FaCheck,
  FaTools, FaTrash, FaUserSlash, FaUnlock,
} from 'react-icons/fa';
import { supabase, supabaseAdmin } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

// Service role key varsa RLS bypass, yoxdursa anon client
const db = supabaseAdmin || supabase;


// ─── helpers ────────────────────────────────────────────────────────────────
function Toast({ msg, type }) {
  return msg ? (
    <div style={{ ...ts.toast, backgroundColor: type === 'error' ? '#fee2e2' : '#dcfce7', color: type === 'error' ? '#dc2626' : '#16a34a' }}>
      {type === 'error' ? '✗' : '✓'} {msg}
    </div>
  ) : null;
}

function SectionCard({ title, icon: Icon, color = '#6366f1', children }) {
  return (
    <div style={ts.sCard}>
      <div style={{ ...ts.sCardHeader, borderLeft: `4px solid ${color}` }}>
        <Icon size={16} color={color} />
        <h3 style={ts.sCardTitle}>{title}</h3>
      </div>
      <div style={ts.sCardBody}>{children}</div>
    </div>
  );
}

function SearchDropdown({ results, onSelect, renderItem }) {
  if (!results.length) return null;
  return (
    <div style={ts.dropdown}>
      {results.map((r, i) => (
        <div key={i} style={ts.dropdownItem} onClick={() => onSelect(r)}>{renderItem(r)}</div>
      ))}
    </div>
  );
}

export default function InterventionsPage() {
  const { admin } = useAuth();
  const [toast, setToast] = useState({ msg: '', type: 'success' });
  const [loading, setLoading] = useState({});

  // Notification state
  const [notifTarget, setNotifTarget] = useState('all_users');
  const [notifSearch, setNotifSearch] = useState('');
  const [notifSearchResults, setNotifSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const [notifType, setNotifType] = useState('system');

  // Points adjustment
  const [pointsSearch, setPointsSearch] = useState('');
  const [pointsResults, setPointsResults] = useState([]);
  const [pointsUser, setPointsUser] = useState(null);
  const [pointsDelta, setPointsDelta] = useState('');
  const [pointsReason, setPointsReason] = useState('');

  // Badge grant
  const [badgeSearch, setBadgeSearch] = useState('');
  const [badgeResults, setBadgeResults] = useState([]);
  const [badgeUser, setBadgeUser] = useState(null);
  const [badgeId, setBadgeId] = useState('');
  const [badges, setBadges] = useState([]);

  React.useEffect(() => {
    db.from('badges').select('id, name').order('name').then(({ data }) => setBadges(data || []));
  }, []);

  // Subscription
  const [subBizSearch, setSubBizSearch] = useState('');
  const [subResults, setSubResults] = useState([]);
  const [subBiz, setSubBiz] = useState(null);
  const [subMonths, setSubMonths] = useState('1');

  // Ban state
  const [banSearch, setBanSearch] = useState('');
  const [banResults, setBanResults] = useState([]);
  const [banUser, setBanUser] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [banDuration, setBanDuration] = useState('permanent');

  // Unban state
  const [unbanSearch, setUnbanSearch] = useState('');
  const [unbanResults, setUnbanResults] = useState([]);
  const [unbanUser, setUnbanUser] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3500);
  };

  const setLoad = (key, val) => setLoading(prev => ({ ...prev, [key]: val }));

  // ── Search helpers ────────────────────────────────────────────────────────
  const searchUsers = async (q, setResults) => {
    if (!q.trim()) { setResults([]); return; }
    const { data } = await supabase
      .from('users')
      .select('id, full_name, email, points, is_banned, ban_reason')
      .or(`full_name.ilike.%${q}%,email.ilike.%${q}%`)
      .limit(6);
    setResults(data || []);
  };

  const searchBusinesses = async (q, setResults) => {
    if (!q.trim()) { setResults([]); return; }
    const { data } = await supabase
      .from('businesses')
      .select('id, name, subscription_end')
      .ilike('name', `%${q}%`)
      .limit(6);
    setResults(data || []);
  };

  // ── Log action ────────────────────────────────────────────────────────────
  const logAction = async (action, targetType, targetId, details) => {
    try {
      await db.from('system_logs').insert({
        admin_id: admin?.id,
        action,
        target_type: targetType,
        target_id: targetId ? String(targetId) : null,
        details: details || {},
        created_at: new Date().toISOString(),
      });
    } catch (_) { /* silent */ }
  };

  // ── Send push via Supabase RPC (server-side, no CORS) ────────────────────
  // Browser-dən Expo push API-ə birbaşa müraciət CORS-a görə bloklanır.
  // Supabase Edge Function / pg_net üzərindən göndərilir.
  const sendPushViaRpc = async (userIds, title, body) => {
    if (!userIds.length) return 0;
    const { data: tokenRows } = await db
      .from('push_tokens')
      .select('user_id, token')
      .in('user_id', userIds.slice(0, 1000));
    const rows = (tokenRows || []).filter(r => r.token);
    let sent = 0;
    for (const { token } of rows) {
      try {
        await db.rpc('send_expo_push', {
          p_token: token, p_title: title, p_body: body, p_data: {},
        });
        sent++;
      } catch (_) { /* silent */ }
    }
    return sent;
  };

  // ── Send Notification ─────────────────────────────────────────────────────
  const sendNotification = async () => {
    if (!notifTitle.trim() || !notifBody.trim()) { showToast('Başlıq və mətn daxil edin', 'error'); return; }
    setLoad('notif', true);
    try {
      let userIds = [];

      if (notifTarget === 'all_users') {
        const { data: allUsers } = await db.from('users').select('id');
        userIds = (allUsers || []).map(u => u.id);
      } else if (notifTarget === 'specific_user' && selectedUser) {
        userIds = [selectedUser.id];
      } else if (notifTarget === 'all_businesses') {
        const { data: bizOwners } = await db.from('businesses').select('owner_id').eq('is_active', true);
        userIds = [...new Set((bizOwners || []).map(b => b.owner_id).filter(Boolean))];
      } else {
        showToast('Hədəf seçin', 'error'); setLoad('notif', false); return;
      }

      if (!userIds.length) { showToast('Uyğun alıcı tapılmadı', 'error'); setLoad('notif', false); return; }

      // DB notifications insert — FK xətası olanlara görə ayrı-ayrı insert, xətalıları atla
      const rows = userIds.map(id => ({
        user_id: id, type: notifType, title: notifTitle, body: notifBody, is_read: false,
      }));
      let dbSent = 0;
      for (const row of rows) {
        try {
          const { error: insErr } = await db.from('notifications').insert(row);
          if (!insErr) dbSent++;
        } catch (_) { /* FK xətası — bu user auth.users-da yoxdur, atla */ }
      }

      // Push — Supabase RPC vasitəsilə (CORS yoxdur)
      const pushSent = await sendPushViaRpc(userIds, notifTitle, notifBody);

      await logAction('send_notification', 'notification', null, {
        target: notifTarget, title: notifTitle, db_count: dbSent, push_count: pushSent,
      });
      showToast(`✓ ${dbSent}/${rows.length} alıcıya bildiriş, ${pushSent} cihaza push göndərildi`);
      setNotifTitle(''); setNotifBody(''); setSelectedUser(null); setNotifSearch('');
    } catch (e) {
      showToast('Xəta: ' + e.message, 'error');
    }
    setLoad('notif', false);
  };

  // ── Points adjustment ─────────────────────────────────────────────────────
  const adjustPoints = async () => {
    if (!pointsUser) { showToast('İstifadəçi seçin', 'error'); return; }
    const delta = parseInt(pointsDelta);
    if (isNaN(delta) || delta === 0) { showToast('Sıfırdan fərqli rəqəm daxil edin', 'error'); return; }
    setLoad('points', true);
    try {
      const newPoints = Math.max(0, (pointsUser.points || 0) + delta);

      // users.points yenilə
      const { error: updateErr } = await db
        .from('users')
        .update({ points: newPoints })
        .eq('id', pointsUser.id);
      if (updateErr) throw updateErr;

      // point_transactions loqu yaz
      await db.from('point_transactions').insert({
        user_id: pointsUser.id,
        points: delta,
        reason: (pointsReason || 'Admin manual tənzimlənməsi') + ` (${delta > 0 ? '+' : ''}${delta})`,
        created_at: new Date().toISOString(),
      });

      await logAction('adjust_points', 'user', pointsUser.id, { delta, reason: pointsReason, new_total: newPoints });
      showToast(`${delta > 0 ? '+' : ''}${delta} xal tətbiq edildi → cəmi: ${newPoints}`);
      setPointsUser(prev => ({ ...prev, points: newPoints }));
      setPointsDelta(''); setPointsReason('');
    } catch (e) {
      showToast('Xəta: ' + e.message, 'error');
    }
    setLoad('points', false);
  };

  // ── Badge grant ───────────────────────────────────────────────────────────
  const grantBadge = async () => {
    if (!badgeUser || !badgeId) { showToast('İstifadəçi və badge seçin', 'error'); return; }
    setLoad('badge', true);
    try {
      const { error } = await db
        .from('user_badges')
        .upsert(
          { user_id: badgeUser.id, badge_id: badgeId, earned_at: new Date().toISOString() },
          { onConflict: 'user_id,badge_id' }
        );
      if (error) throw error;
      const badgeName = badges.find(b => b.id === badgeId)?.name || badgeId;
      await logAction('grant_badge', 'user', badgeUser.id, { badge_id: badgeId, badge_name: badgeName });
      showToast(`"${badgeName}" badge verildi`);
      setBadgeUser(null); setBadgeId(''); setBadgeSearch('');
    } catch (e) {
      showToast('Xəta: ' + e.message, 'error');
    }
    setLoad('badge', false);
  };

  // ── Subscription extend ───────────────────────────────────────────────────
  const extendSubscription = async () => {
    if (!subBiz) { showToast('Biznes seçin', 'error'); return; }
    const months = parseInt(subMonths);
    if (isNaN(months) || months < 1) { showToast('Etibarlı ay sayı daxil edin', 'error'); return; }
    setLoad('sub', true);
    try {
      const base = subBiz.subscription_end ? new Date(subBiz.subscription_end) : new Date();
      if (base < new Date()) base.setTime(Date.now());
      base.setMonth(base.getMonth() + months);
      const { error } = await db
        .from('businesses')
        .update({ subscription_end: base.toISOString(), is_active: true })
        .eq('id', subBiz.id);
      if (error) throw error;
      await logAction('extend_subscription', 'business', subBiz.id, { months, new_end: base.toISOString() });
      showToast(`Abunəlik ${months} ay uzadıldı → ${base.toLocaleDateString('az-AZ')}`);
      setSubBiz(null); setSubBizSearch(''); setSubMonths('1');
    } catch (e) {
      showToast('Xəta: ' + e.message, 'error');
    }
    setLoad('sub', false);
  };

  const forceExpireSubscription = async () => {
    if (!subBiz) { showToast('Biznes seçin', 'error'); return; }
    if (!window.confirm(`"${subBiz.name}" biznesi deaktiv edilsin?`)) return;
    setLoad('expire', true);
    try {
      const { error } = await db
        .from('businesses')
        .update({ subscription_end: new Date().toISOString(), is_active: false })
        .eq('id', subBiz.id);
      if (error) throw error;
      await logAction('force_expire_subscription', 'business', subBiz.id, {});
      showToast('Abunəlik dayandırıldı — biznes deaktiv');
      setSubBiz(null); setSubBizSearch('');
    } catch (e) {
      showToast('Xəta: ' + e.message, 'error');
    }
    setLoad('expire', false);
  };

  // ── Ban user ──────────────────────────────────────────────────────────────
  const handleBanUser = async () => {
    if (!banUser) { showToast('İstifadəçi seçin', 'error'); return; }
    if (!banReason.trim()) { showToast('Ban səbəbi daxil edin', 'error'); return; }
    if (!window.confirm(`"${banUser.full_name || banUser.email}" ban edilsin?`)) return;
    setLoad('ban', true);
    try {
      let bannedUntil = null;
      if (banDuration === '7days') {
        bannedUntil = new Date(Date.now() + 7 * 86400000).toISOString();
      } else if (banDuration === '30days') {
        bannedUntil = new Date(Date.now() + 30 * 86400000).toISOString();
      }
      const { error } = await db
        .from('users')
        .update({ is_banned: true, ban_reason: banReason, banned_until: bannedUntil })
        .eq('id', banUser.id);
      if (error) throw error;

      try {
        await db.from('notifications').insert({
          user_id: banUser.id, type: 'system',
          title: 'Hesabınız bloklanıb',
          body: `Səbəb: ${banReason}`,
          is_read: false,
        });
      } catch (_) { /* silent */ }

      await logAction('ban_user', 'user', banUser.id, { reason: banReason, duration: banDuration, banned_until: bannedUntil });
      showToast(`${banUser.full_name || banUser.email} ban edildi`);
      setBanUser(null); setBanSearch(''); setBanReason(''); setBanDuration('permanent');
    } catch (e) {
      showToast('Xəta: ' + e.message, 'error');
    }
    setLoad('ban', false);
  };

  // ── Unban user ────────────────────────────────────────────────────────────
  const handleUnbanUser = async () => {
    if (!unbanUser) { showToast('İstifadəçi seçin', 'error'); return; }
    if (!window.confirm(`"${unbanUser.full_name || unbanUser.email}" banı açılsın?`)) return;
    setLoad('unban', true);
    try {
      const { error } = await db
        .from('users')
        .update({ is_banned: false, ban_reason: null, banned_until: null })
        .eq('id', unbanUser.id);
      if (error) throw error;

      try {
        await db.from('notifications').insert({
          user_id: unbanUser.id, type: 'system',
          title: 'Hesabınız aktiv edildi',
          body: 'Hesabınızdakı məhdudiyyət götürüldü.',
          is_read: false,
        });
      } catch (_) { /* silent */ }

      await logAction('unban_user', 'user', unbanUser.id, {});
      showToast(`${unbanUser.full_name || unbanUser.email} banı açıldı`);
      setUnbanUser(null); setUnbanSearch('');
    } catch (e) {
      showToast('Xəta: ' + e.message, 'error');
    }
    setLoad('unban', false);
  };

  // ── Purge old check-ins ───────────────────────────────────────────────────
  const purgeOldCheckIns = async (days) => {
    if (!window.confirm(`${days} gündən köhnə check-inlər silinsin?`)) return;
    setLoad('purge', true);
    const cutoff = new Date(Date.now() - days * 86400000).toISOString();
    const { error, count } = await db
      .from('check_ins')
      .delete({ count: 'exact' })
      .lt('created_at', cutoff);
    if (!error) {
      await logAction('purge_checkins', 'system', null, { days, cutoff });
      showToast(`${count || 0} köhnə check-in silindi`);
    } else {
      showToast('Xəta: ' + error.message, 'error');
    }
    setLoad('purge', false);
  };

  const resolveAllFraud = async () => {
    if (!window.confirm('Bütün həll edilməmiş fraud xəbərdarlıqları "resolved" edilsin?')) return;
    setLoad('fraud', true);
    const { error } = await db.from('fraud_logs').update({ resolved: true }).eq('resolved', false);
    if (!error) {
      await logAction('resolve_all_fraud', 'system', null, {});
      showToast('Bütün fraud xəbərdarlıqları həll edildi');
    } else showToast('Xəta: ' + error.message, 'error');
    setLoad('fraud', false);
  };

  return (
    <div style={ts.page}>
      <Toast msg={toast.msg} type={toast.type} />

      <div style={ts.header}>
        <h1 style={ts.title}><FaTools size={20} color="#6366f1" /> Müdaxilələr</h1>
        <p style={ts.sub}>Sistemə birbaşa müdaxilə, bildiriş göndərmə və manual tənzimləmələr</p>
      </div>

      <div style={ts.grid}>

        {/* ── Send Notification ──────────────────────────────────────────── */}
        <SectionCard title="Bildiriş Göndər" icon={FaBell} color="#6366f1">
          <div style={ts.field}>
            <label style={ts.label}>Hədəf</label>
            <select value={notifTarget} onChange={e => { setNotifTarget(e.target.value); setSelectedUser(null); setNotifSearch(''); setNotifSearchResults([]); }} style={ts.select}>
              <option value="all_users">Bütün istifadəçilər</option>
              <option value="all_businesses">Bütün biznes sahibləri</option>
              <option value="specific_user">Xüsusi istifadəçi</option>
            </select>
          </div>
          {notifTarget === 'specific_user' && (
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <div style={ts.searchRow}>
                <FaSearch size={12} color="#94a3b8" />
                <input
                  style={ts.searchInput}
                  placeholder="Ad və ya email..."
                  value={selectedUser ? `${selectedUser.full_name || selectedUser.email}` : notifSearch}
                  onChange={e => { setNotifSearch(e.target.value); setSelectedUser(null); searchUsers(e.target.value, setNotifSearchResults); }}
                />
                {selectedUser && <span style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 16 }} onClick={() => { setSelectedUser(null); setNotifSearch(''); }}>×</span>}
              </div>
              <SearchDropdown
                results={notifSearchResults}
                onSelect={u => { setSelectedUser(u); setNotifSearchResults([]); setNotifSearch(''); }}
                renderItem={u => <>{u.full_name || '—'} <span style={{ color: '#94a3b8', fontSize: 12 }}>{u.email}</span></>}
              />
            </div>
          )}
          {selectedUser && (
            <div style={ts.selectedChip}>
              <FaCheck size={11} color="#22c55e" />
              <strong>{selectedUser.full_name || selectedUser.email}</strong>
            </div>
          )}
          <div style={ts.field}>
            <label style={ts.label}>Tip</label>
            <select value={notifType} onChange={e => setNotifType(e.target.value)} style={ts.select}>
              <option value="system">⚙️ Sistem</option>
              <option value="campaign">🎁 Kampaniya</option>
              <option value="reservation">📅 Rezervasiya</option>
              <option value="points">🪙 Xal</option>
              <option value="meetup">👥 Görüş</option>
              <option value="follow">🔔 Follow</option>
            </select>
          </div>
          <div style={ts.field}>
            <label style={ts.label}>Başlıq</label>
            <input value={notifTitle} onChange={e => setNotifTitle(e.target.value)} style={ts.input} placeholder="Bildiriş başlığı..." />
          </div>
          <div style={ts.field}>
            <label style={ts.label}>Mətn</label>
            <textarea value={notifBody} onChange={e => setNotifBody(e.target.value)} style={ts.textarea} rows={3} placeholder="Bildiriş mətni..." />
          </div>
          <button onClick={sendNotification} disabled={loading.notif} style={{ ...ts.btn, width: '100%' }}>
            <FaBell size={13} /> {loading.notif ? 'Göndərilir...' : 'Göndər (DB + Push)'}
          </button>
        </SectionCard>

        {/* ── Points Adjustment ─────────────────────────────────────────── */}
        <SectionCard title="Xal Tənzimlənməsi" icon={FaCoins} color="#f59e0b">
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <div style={ts.searchRow}>
              <FaSearch size={12} color="#94a3b8" />
              <input
                style={ts.searchInput}
                placeholder="İstifadəçi axtar..."
                value={pointsUser ? `${pointsUser.full_name || pointsUser.email}` : pointsSearch}
                onChange={e => { setPointsSearch(e.target.value); setPointsUser(null); searchUsers(e.target.value, setPointsResults); }}
              />
              {pointsUser && <span style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 16 }} onClick={() => { setPointsUser(null); setPointsSearch(''); }}>×</span>}
            </div>
            <SearchDropdown
              results={pointsResults}
              onSelect={u => { setPointsUser(u); setPointsResults([]); setPointsSearch(''); }}
              renderItem={u => <>{u.full_name || '—'} <span style={{ color: '#94a3b8' }}>({u.points || 0} xal)</span></>}
            />
          </div>
          {pointsUser && (
            <div style={ts.selectedChip}>
              <FaCheck size={11} color="#22c55e" />
              <strong>{pointsUser.full_name || pointsUser.email}</strong>
              <span style={{ color: '#64748b' }}>— cari: {pointsUser.points || 0} xal</span>
            </div>
          )}
          <div style={ts.twoFields}>
            <div style={ts.field}>
              <label style={ts.label}>Dəyişim (+/-)</label>
              <input value={pointsDelta} onChange={e => setPointsDelta(e.target.value)} style={ts.input} placeholder="+100 və ya -50" type="number" />
            </div>
            <div style={ts.field}>
              <label style={ts.label}>Səbəb</label>
              <input value={pointsReason} onChange={e => setPointsReason(e.target.value)} style={ts.input} placeholder="Könüllü..." />
            </div>
          </div>
          <button onClick={adjustPoints} disabled={loading.points} style={{ ...ts.btn, width: '100%', backgroundColor: '#f59e0b' }}>
            <FaCoins size={13} /> {loading.points ? 'Tətbiq edilir...' : 'Tətbiq Et'}
          </button>
        </SectionCard>

        {/* ── Badge Grant ───────────────────────────────────────────────── */}
        <SectionCard title="Badge Ver" icon={FaMedal} color="#8b5cf6">
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <div style={ts.searchRow}>
              <FaSearch size={12} color="#94a3b8" />
              <input
                style={ts.searchInput}
                placeholder="İstifadəçi axtar..."
                value={badgeUser ? `${badgeUser.full_name || badgeUser.email}` : badgeSearch}
                onChange={e => { setBadgeSearch(e.target.value); setBadgeUser(null); searchUsers(e.target.value, setBadgeResults); }}
              />
              {badgeUser && <span style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 16 }} onClick={() => { setBadgeUser(null); setBadgeSearch(''); }}>×</span>}
            </div>
            <SearchDropdown
              results={badgeResults}
              onSelect={u => { setBadgeUser(u); setBadgeResults([]); setBadgeSearch(''); }}
              renderItem={u => <>{u.full_name || '—'} <span style={{ color: '#94a3b8', fontSize: 12 }}>{u.email}</span></>}
            />
          </div>
          {badgeUser && (
            <div style={ts.selectedChip}>
              <FaCheck size={11} color="#22c55e" /> <strong>{badgeUser.full_name || badgeUser.email}</strong>
            </div>
          )}
          <div style={ts.field}>
            <label style={ts.label}>Badge</label>
            <select value={badgeId} onChange={e => setBadgeId(e.target.value)} style={ts.select}>
              <option value="">Seçin...</option>
              {badges.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <button onClick={grantBadge} disabled={loading.badge} style={{ ...ts.btn, width: '100%', backgroundColor: '#8b5cf6' }}>
            <FaMedal size={13} /> {loading.badge ? 'Verilir...' : 'Badge Ver'}
          </button>
        </SectionCard>

        {/* ── Subscription Management ───────────────────────────────────── */}
        <SectionCard title="Abunəlik İdarəetməsi" icon={FaCalendarTimes} color="#10b981">
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <div style={ts.searchRow}>
              <FaSearch size={12} color="#94a3b8" />
              <input
                style={ts.searchInput}
                placeholder="Biznes axtar..."
                value={subBiz ? subBiz.name : subBizSearch}
                onChange={e => { setSubBizSearch(e.target.value); setSubBiz(null); searchBusinesses(e.target.value, setSubResults); }}
              />
              {subBiz && <span style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 16 }} onClick={() => { setSubBiz(null); setSubBizSearch(''); }}>×</span>}
            </div>
            <SearchDropdown
              results={subResults}
              onSelect={b => { setSubBiz(b); setSubResults([]); setSubBizSearch(''); }}
              renderItem={b => <>{b.name} <span style={{ color: '#94a3b8', fontSize: 12 }}>{b.subscription_end ? `→ ${new Date(b.subscription_end).toLocaleDateString('az-AZ')}` : 'tarix yoxdur'}</span></>}
            />
          </div>
          {subBiz && (
            <div style={ts.selectedChip}>
              <FaCheck size={11} color="#22c55e" /> <strong>{subBiz.name}</strong>
              {subBiz.subscription_end && <span style={{ color: '#64748b', fontSize: 12 }}>— bitmə: {new Date(subBiz.subscription_end).toLocaleDateString('az-AZ')}</span>}
            </div>
          )}
          <div style={ts.field}>
            <label style={ts.label}>Uzatma (ay)</label>
            <input value={subMonths} onChange={e => setSubMonths(e.target.value)} style={ts.input} type="number" min="1" max="24" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button onClick={extendSubscription} disabled={loading.sub} style={{ ...ts.btn, backgroundColor: '#10b981' }}>
              {loading.sub ? '...' : 'Uzat'}
            </button>
            <button onClick={forceExpireSubscription} disabled={loading.expire} style={{ ...ts.btn, backgroundColor: '#ef4444' }}>
              <FaBan size={12} /> {loading.expire ? '...' : 'Dayandır'}
            </button>
          </div>
        </SectionCard>

        {/* ── Ban / Unban User ──────────────────────────────────────────── */}
        <SectionCard title="İstifadəçi Ban / Unban" icon={FaUserSlash} color="#ef4444">
          {/* Ban */}
          <p style={ts.subLabel}>Ban et</p>
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <div style={ts.searchRow}>
              <FaSearch size={12} color="#94a3b8" />
              <input
                style={ts.searchInput}
                placeholder="İstifadəçi axtar..."
                value={banUser ? `${banUser.full_name || banUser.email}` : banSearch}
                onChange={e => { setBanSearch(e.target.value); setBanUser(null); searchUsers(e.target.value, setBanResults); }}
              />
              {banUser && <span style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 16 }} onClick={() => { setBanUser(null); setBanSearch(''); }}>×</span>}
            </div>
            <SearchDropdown
              results={banResults}
              onSelect={u => { setBanUser(u); setBanResults([]); setBanSearch(''); }}
              renderItem={u => (
                <>{u.full_name || '—'} <span style={{ color: '#94a3b8', fontSize: 12 }}>{u.email}</span> {u.is_banned && <span style={{ color: '#ef4444', fontSize: 11 }}>⚑ Artıq ban</span>}</>
              )}
            />
          </div>
          {banUser && (
            <div style={{ ...ts.selectedChip, borderColor: banUser.is_banned ? '#fca5a5' : '#bbf7d0', backgroundColor: banUser.is_banned ? '#fef2f2' : '#f0fdf4' }}>
              <FaCheck size={11} color={banUser.is_banned ? '#ef4444' : '#22c55e'} />
              <strong>{banUser.full_name || banUser.email}</strong>
              {banUser.is_banned && <span style={{ color: '#ef4444', fontSize: 12 }}>— artıq ban var</span>}
            </div>
          )}
          <div style={ts.twoFields}>
            <div style={ts.field}>
              <label style={ts.label}>Müddət</label>
              <select value={banDuration} onChange={e => setBanDuration(e.target.value)} style={ts.select}>
                <option value="7days">7 gün</option>
                <option value="30days">30 gün</option>
                <option value="permanent">Daimi</option>
              </select>
            </div>
            <div style={ts.field}>
              <label style={ts.label}>Səbəb</label>
              <input value={banReason} onChange={e => setBanReason(e.target.value)} style={ts.input} placeholder="Spam, fake..." />
            </div>
          </div>
          <button onClick={handleBanUser} disabled={loading.ban} style={{ ...ts.btn, width: '100%', backgroundColor: '#ef4444', marginBottom: 16 }}>
            <FaBan size={13} /> {loading.ban ? 'Ban edilir...' : 'Ban Et'}
          </button>

          {/* Unban */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            <p style={ts.subLabel}>Ban aç</p>
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <div style={ts.searchRow}>
                <FaSearch size={12} color="#94a3b8" />
                <input
                  style={ts.searchInput}
                  placeholder="Banlı istifadəçi axtar..."
                  value={unbanUser ? `${unbanUser.full_name || unbanUser.email}` : unbanSearch}
                  onChange={e => { setUnbanSearch(e.target.value); setUnbanUser(null); searchUsers(e.target.value, setUnbanResults); }}
                />
                {unbanUser && <span style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 16 }} onClick={() => { setUnbanUser(null); setUnbanSearch(''); }}>×</span>}
              </div>
              <SearchDropdown
                results={unbanResults}
                onSelect={u => { setUnbanUser(u); setUnbanResults([]); setUnbanSearch(''); }}
                renderItem={u => (
                  <>{u.full_name || '—'} <span style={{ color: '#94a3b8', fontSize: 12 }}>{u.email}</span> {u.is_banned && <span style={{ color: '#ef4444', fontSize: 11 }}>⚑ Ban</span>}</>
                )}
              />
            </div>
            {unbanUser && (
              <div style={ts.selectedChip}>
                <FaCheck size={11} color="#22c55e" /> <strong>{unbanUser.full_name || unbanUser.email}</strong>
                {unbanUser.ban_reason && <span style={{ color: '#64748b', fontSize: 12 }}>— {unbanUser.ban_reason}</span>}
              </div>
            )}
            <button onClick={handleUnbanUser} disabled={loading.unban} style={{ ...ts.btn, width: '100%', backgroundColor: '#22c55e' }}>
              <FaUnlock size={13} /> {loading.unban ? 'Açılır...' : 'Banı Aç'}
            </button>
          </div>
        </SectionCard>

        {/* ── Security ─────────────────────────────────────────────────── */}
        <SectionCard title="Təhlükəsizlik Əməliyyatları" icon={FaShieldAlt} color="#0ea5e9">
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
            Şübhəli davranış zamanı tez müdaxilə alətləri.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button onClick={() => purgeOldCheckIns(30)} disabled={loading.purge} style={{ ...ts.btn, backgroundColor: '#0ea5e9' }}>
              <FaTrash size={12} /> {loading.purge ? 'Silinir...' : '30 gündən köhnə check-inləri sil'}
            </button>
            <button onClick={() => purgeOldCheckIns(7)} disabled={loading.purge} style={{ ...ts.btnDanger }}>
              <FaTrash size={12} /> 7 gündən köhnə check-inləri sil (test üçün)
            </button>
            <button onClick={resolveAllFraud} disabled={loading.fraud} style={{ ...ts.btn, backgroundColor: '#64748b' }}>
              <FaCheck size={12} /> {loading.fraud ? '...' : 'Bütün fraud xəbərdarlıqlarını həll et'}
            </button>
          </div>
        </SectionCard>

      </div>
    </div>
  );
}

const ts = {
  page: { padding: 28, minHeight: '100vh', backgroundColor: '#f8fafc' },
  header: { marginBottom: 28 },
  title: { fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  sub: { fontSize: 13, color: '#64748b', marginTop: 6 },
  subLabel: { fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 },
  sCard: { backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  sCardHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', backgroundColor: '#f8fafc', borderBottom: '1px solid #f1f5f9', paddingLeft: 16 },
  sCardTitle: { fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 },
  sCardBody: { padding: 20 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 5 },
  input: { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', boxSizing: 'border-box', outline: 'none' },
  select: { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', boxSizing: 'border-box', outline: 'none', backgroundColor: '#fff' },
  textarea: { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit' },
  btn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '9px 16px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  btnDanger: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '9px 16px', backgroundColor: '#fff', color: '#ef4444', border: '1.5px solid #ef4444', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  twoFields: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  searchRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, backgroundColor: '#fff' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#1e293b', minWidth: 0 },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden' },
  dropdownItem: { padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#1e293b', borderBottom: '1px solid #f8fafc' },
  selectedChip: { display: 'flex', alignItems: 'center', gap: 6, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '7px 12px', fontSize: 13, marginBottom: 12 },
  toast: { position: 'fixed', bottom: 24, right: 24, padding: '12px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
};
